import { ModalVoucherDetail } from 'components-v2/organisms/pages/voucher-detail/ModalVoucherDetail';
import { useRouter } from 'next/router';
import React, { createContext, useEffect, useState } from 'react';

interface PopupVoucherContext {
  open: boolean;
  setOpen: (open: boolean) => void;
  code: string;
  setCode: (code: string) => void;
  handleOpen: (code: string, callback?: () => void, onCloseCallback?: () => void) => void;
  handleClose: () => void;
}

export const PopupVoucherContext = createContext<PopupVoucherContext | null>(null);

export const PopupVoucherProvider: React.FC = ({ children }) => {

  const router = useRouter();

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');

  const [onCloseCallbacks, setOnCloseCallbacks] = useState<any[]>([]);

  /**
   * Opens the popup voucher with the given code and optionally registers a callback to be executed when the popup is closed.
   *
   * @param {string} code - The voucher code to be set and displayed in the popup.
   * @param {() => void} [callback] - An optional callback function to be executed when the popup is closed.
   */
  const handleOpen = (code: string, callback?: () => void, onCloseCallback?: () => void) => {
    setCode(code);
    setOpen(true);

    callback && callback();

    onCloseCallback && setOnCloseCallbacks((prev) => [...prev, onCloseCallback]);
  };

  const handleClose = () => {
    setOpen(false);
    setCode('');

    if (onCloseCallbacks) {
      onCloseCallbacks.forEach((callback) => typeof callback === 'function' && callback());
      setOnCloseCallbacks([]);
    }
  };

  useEffect(() => {
    if(open) handleClose();
  }, [router]);

  return (
    <PopupVoucherContext.Provider value={{ open, setOpen, code, setCode, handleOpen, handleClose }}>
      <>
        {children}
        <ModalVoucherDetail open={open} code={code} onClose={handleClose} />
      </>
    </PopupVoucherContext.Provider>
  );
};

export const usePopupVoucherContext = () => {
  const context = React.useContext(PopupVoucherContext);
  if (!context) {
    throw new Error('usePopupVoucher must be used within a PopupVoucherProvider');
  }
  return context;
};

